/**
 * sqliteInit — Conditional SQLite backend activation for Capacitor native.
 *
 * Called BEFORE db.open() to optionally replace IndexedDB with SQLite.
 * Uses localStorage flag `dev:db-backend` for dev-mode switching:
 *   - "idb"    → always use IndexedDB (default on web)
 *   - "sqlite" → always use SQLite (default on native)
 *   - "auto"   → native=SQLite, web=IDB (production default)
 *
 * The flag is read synchronously, but driver creation is async —
 * this function returns a promise that must resolve before db.open().
 */

// @ts-ignore TEMPORARY: Capacitor used by resolveBackend when SQLite is re-enabled
import { Capacitor } from "@capacitor/core"
import { setSqliteActive } from "./sqliteDetect"

const LS_KEY = "dev:db-backend"

export type DbBackend = "auto" | "idb" | "sqlite"

/**
 * Read the current backend preference.
 */
export function getDbBackendPref(): DbBackend {
  try {
    const val = localStorage.getItem(LS_KEY)
    if (val === "idb" || val === "sqlite" || val === "auto") return val
  } catch {
    /* SSR / restricted */
  }
  return "auto"
}

/**
 * Set the backend preference. Takes effect on next app restart.
 */
export function setDbBackendPref(pref: DbBackend): void {
  try {
    localStorage.setItem(LS_KEY, pref)
  } catch {
    /* noop */
  }
}

/**
 * Resolve "auto" to the actual backend for the current platform.
 */
export function resolveBackend(_pref: DbBackend): "idb" | "sqlite" {
  // TEMPORARY: Force IDB everywhere while debugging other app issues.
  // Remove this override to re-enable SQLite on native.
  return "idb"
}

/**
 * Conditionally initialize the SQLite backend on the given Dexie instance.
 * MUST be called before db.open().
 *
 * Returns the actual backend used ("idb" or "sqlite").
 */
export async function initSqliteIfNeeded(
  db: import("dexie").default,
): Promise<"idb" | "sqlite"> {
  const pref = getDbBackendPref()
  const backend = resolveBackend(pref)

  if (backend === "idb") {
    setSqliteActive(false)
    console.info("[DB] Backend: IndexedDB")
    return "idb"
  }

  try {
    // Dynamic import — only loads the SQLite addon and driver code when needed.
    // This keeps the web bundle clean when SQLite isn't used.
    const [{ dexieSqlite }, { CapacitorSqliteDriver }] = await Promise.all([
      import("dexie-sqlite"),
      import("dexie-sqlite/capacitor"),
    ])

    // Guard: if the native plugin is missing (not in SPM/Podfile), the bridge
    // call can hang forever. Race against a timeout to avoid blocking db.open().
    const DRIVER_TIMEOUT_MS = 5_000
    const driver = await Promise.race([
      CapacitorSqliteDriver.create("ToToDoDb"),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("SQLite driver creation timed out")), DRIVER_TIMEOUT_MS),
      ),
    ])
    const addon = dexieSqlite({ driver })

    // Apply the addon — injects fake-indexeddb and registers DBCore middleware
    addon(db)

    setSqliteActive(true)
    console.info("[DB] Backend: SQLite (Capacitor native)")
    return "sqlite"
  } catch (e) {
    // Plugin not available (e.g. not synced yet) — fall back to IDB
    console.warn("[DB] SQLite init failed, falling back to IndexedDB:", e)
    setSqliteActive(false)
    return "idb"
  }
}
